import PropTypes from 'prop-types';

const addSeparators = function (nStr: string | number, thousandsSep: string, decimalSep: string) {
    const x = String(nStr).split('.');
    let x1 = x[0];
    const x2 = x.length > 1 ? decimalSep + x[1] : '';
    const rgx = /(\d+)(\d{3})/;
    while (rgx.test(x1)) {
        x1 = x1.replace(rgx, '$1' + thousandsSep + '$2');
    }
    return x1 + x2;
};

export const numberFormat = function (opts_in?: any) {
    const defaults = {
        digitsAfterDecimal: 2,
        scaler: 1,
        thousandsSep: ',',
        decimalSep: '.',
        prefix: '',
        suffix: '',
    };
    const opts = Object.assign({}, defaults, opts_in);
    return function (x: number) {
        if (isNaN(x) || !isFinite(x)) {
            return '';
        }
        const result = addSeparators(
            (opts.scaler * x).toFixed(opts.digitsAfterDecimal),
            opts.thousandsSep,
            opts.decimalSep
        );
        return '' + opts.prefix + result + opts.suffix;
    };
};

const rx = /(\d+)|(\D+)/g;
const rd = /\d/;
const rz = /^0/;
export const naturalSort = function (as: any, bs: any): number {
    if (bs !== null && as === null) {
        return -1;
    }
    if (as !== null && bs === null) {
        return 1;
    }
    if (typeof as === 'number' && isNaN(as)) {
        return -1;
    }
    if (typeof bs === 'number' && isNaN(bs)) {
        return 1;
    }
    const nas = Number(as);
    const nbs = Number(bs);
    if (nas < nbs) {
        return -1;
    }
    if (nas > nbs) {
        return 1;
    }
    if (typeof as === 'number' && typeof bs !== 'number') {
        return -1;
    }
    if (typeof bs === 'number' && typeof as !== 'number') {
        return 1;
    }
    if (typeof as === 'number' && typeof bs === 'number') {
        return 0;
    }
    if (isNaN(nbs) && !isNaN(nas)) {
        return -1;
    }
    if (isNaN(nas) && !isNaN(nbs)) {
        return 1;
    }
    let a = String(as);
    let b = String(bs);
    if (a === b) {
        return 0;
    }
    if (!rd.test(a) || !rd.test(b)) {
        return a > b ? 1 : -1;
    }
    const am = a.match(rx) || [];
    const bm = b.match(rx) || [];
    while (am.length && bm.length) {
        const a1 = am.shift()!;
        const b1 = bm.shift()!;
        if (a1 !== b1) {
            if (rd.test(a1) && rd.test(b1)) {
                return Number(a1.replace(rz, '.0')) - Number(b1.replace(rz, '.0'));
            }
            return a1 > b1 ? 1 : -1;
        }
    }
    return am.length - bm.length;
};

export const sortAs = function (order: any[]) {
    const mapping: any = {};
    const l_mapping: any = {};
    for (const i in order) {
        const x = order[i];
        mapping[x] = i;
        if (typeof x === 'string') {
            l_mapping[x.toLowerCase()] = i;
        }
    }
    return function (a: any, b: any) {
        if (a in mapping && b in mapping) {
            return mapping[a] - mapping[b];
        } else if (a in mapping) {
            return -1;
        } else if (b in mapping) {
            return 1;
        } else if (a in l_mapping && b in l_mapping) {
            return l_mapping[a] - l_mapping[b];
        } else if (a in l_mapping) {
            return -1;
        } else if (b in l_mapping) {
            return 1;
        }
        return naturalSort(a, b);
    };
};

export const getSort = function (sorters: any, attr: string) {
    if (sorters) {
        if (typeof sorters === 'function') {
            const sort = sorters(attr);
            if (typeof sort === 'function') {
                return sort;
            }
        } else if (attr in sorters) {
            return sorters[attr];
        }
    }
    return naturalSort;
};

const usFmt = numberFormat();
const usFmtInt = numberFormat({ digitsAfterDecimal: 0 });
const usFmtPct = numberFormat({
    digitsAfterDecimal: 1,
    scaler: 100,
    suffix: '%',
});
const lotFmt = numberFormat({ digitsAfterDecimal: 0 });

export const aggregatorTemplates: any = {
    count(formatter = usFmtInt) {
        return () => () => ({
            count: 0,
            push() {
                this.count++;
            },
            value() {
                return this.count;
            },
            format: formatter,
        });
    },
    uniques(fn: any, formatter = usFmtInt) {
        return ([attr]: any[]) => () => ({
            uniq: [] as any[],
            push(record: any) {
                if (!Array.from(this.uniq).includes(record[attr])) {
                    this.uniq.push(record[attr]);
                }
            },
            value() {
                return fn(this.uniq);
            },
            format: formatter,
            numInputs: typeof attr !== 'undefined' ? 0 : 1,
        });
    },
    sum(formatter = usFmt) {
        return ([attr]: any[]) => () => ({
            sum: 0,
            push(record: any) {
                if (!isNaN(parseFloat(record[attr]))) {
                    this.sum += parseFloat(record[attr]);
                }
            },
            value() {
                return this.sum;
            },
            format: formatter,
            numInputs: typeof attr !== 'undefined' ? 0 : 1,
        });
    },
    extremes(mode: 'min' | 'max' | 'first' | 'last', formatter = usFmt) {
        return ([attr]: any[]) => (data: any) => ({
            val: null as any,
            sorter: getSort(typeof data !== 'undefined' ? data.sorters : null, attr),
            push(record: any) {
                let x = record[attr];
                if (['min', 'max'].includes(mode)) {
                    x = parseFloat(x);
                    if (!isNaN(x)) {
                        this.val = Math[mode as 'min' | 'max'](x, this.val !== null ? this.val : x);
                    }
                }
                if (
                    mode === 'first' &&
                    this.sorter(x, this.val !== null ? this.val : x) <= 0
                ) {
                    this.val = x;
                }
                if (
                    mode === 'last' &&
                    this.sorter(x, this.val !== null ? this.val : x) >= 0
                ) {
                    this.val = x;
                }
            },
            value() {
                return this.val;
            },
            format(x: any) {
                if (isNaN(x)) {
                    return x;
                }
                return formatter(x);
            },
            numInputs: typeof attr !== 'undefined' ? 0 : 1,
        });
    },
    quantile(q: number, formatter = usFmt) {
        return ([attr]: any[]) => () => ({
            vals: [] as any[],
            push(record: any) {
                const x = parseFloat(record[attr]);
                if (!isNaN(x)) {
                    this.vals.push(x);
                }
            },
            value() {
                if (this.vals.length === 0) {
                    return null;
                }
                this.vals.sort((a, b) => a - b);
                const i = (this.vals.length - 1) * q;
                return (this.vals[Math.floor(i)] + this.vals[Math.ceil(i)]) / 2.0;
            },
            format: formatter,
            numInputs: typeof attr !== 'undefined' ? 0 : 1,
        });
    },
    runningStat(mode = 'mean', ddof = 1, formatter = usFmt) {
        return ([attr]: any[]) => () => ({
            n: 0.0,
            m: 0.0,
            s: 0.0,
            push(record: any) {
                const x = parseFloat(record[attr]);
                if (isNaN(x)) {
                    return;
                }
                this.n += 1.0;
                if (this.n === 1.0) {
                    this.m = x;
                }
                const m_new = this.m + (x - this.m) / this.n;
                this.s = this.s + (x - this.m) * (x - m_new);
                this.m = m_new;
            },
            value() {
                if (mode === 'mean') {
                    if (this.n === 0) {
                        return 0 / 0;
                    }
                    return this.m;
                }
                if (this.n <= ddof) {
                    return 0;
                }
                switch (mode) {
                    case 'var':
                        return this.s / (this.n - ddof);
                    case 'stdev':
                        return Math.sqrt(this.s / (this.n - ddof));
                    default:
                        throw new Error('unknown mode for runningStat');
                }
            },
            format: formatter,
            numInputs: typeof attr !== 'undefined' ? 0 : 1,
        });
    },
    sumOverSum(formatter = usFmt) {
        return ([num, denom]: any[]) => () => ({
            sumNum: 0,
            sumDenom: 0,
            push(record: any) {
                if (!isNaN(parseFloat(record[num]))) {
                    this.sumNum += parseFloat(record[num]);
                }
                if (!isNaN(parseFloat(record[denom]))) {
                    this.sumDenom += parseFloat(record[denom]);
                }
            },
            value() {
                return this.sumNum / this.sumDenom;
            },
            format: formatter,
            numInputs:
                typeof num !== 'undefined' && typeof denom !== 'undefined' ? 0 : 2,
        });
    },
    fractionOf(wrapped: any, type = 'total', formatter = usFmtPct) {
        return (...x: any[]) => (data: any, rowKey: any[], colKey: any[]) => ({
            selector: ({ total: [[], []], row: [rowKey, []], col: [[], colKey] } as any)[type],
            inner: wrapped(...x)(data, rowKey, colKey),
            push(record: any) {
                this.inner.push(record);
            },
            format: formatter,
            value() {
                return (
                    this.inner.value() /
                    data.getAggregator(...this.selector).inner.value()
                );
            },
            numInputs: wrapped(...x)().numInputs,
        });
    },
};

aggregatorTemplates.countUnique = (f: any) =>
    aggregatorTemplates.uniques((x: any[]) => x.length, f);
aggregatorTemplates.listUnique = (s: string) =>
    aggregatorTemplates.uniques(
        (x: any[]) => x.join(s),
        (x: any) => x
    );
aggregatorTemplates.max = (f: any) => aggregatorTemplates.extremes('max', f);
aggregatorTemplates.min = (f: any) => aggregatorTemplates.extremes('min', f);
aggregatorTemplates.first = (f: any) => aggregatorTemplates.extremes('first', f);
aggregatorTemplates.last = (f: any) => aggregatorTemplates.extremes('last', f);
aggregatorTemplates.median = (f: any) => aggregatorTemplates.quantile(0.5, f);
aggregatorTemplates.average = (f: any) => aggregatorTemplates.runningStat('mean', 1, f);
aggregatorTemplates.var = (ddof: any, f: any) =>
    aggregatorTemplates.runningStat('var', ddof, f);
aggregatorTemplates.stdev = (ddof: any, f: any) =>
    aggregatorTemplates.runningStat('stdev', ddof, f);

export const aggregators = ((tpl) => ({
    Count: tpl.count(usFmtInt),
    'Count Unique Values': tpl.countUnique(usFmtInt),
    // 'List Unique Values': tpl.listUnique(', '),
    Sum: tpl.sum(lotFmt),
    // 'Integer Sum': tpl.sum(usFmtInt),
    Average: tpl.average(lotFmt),
    Median: tpl.median(lotFmt),
    'Sample Variance': tpl.var(1, usFmt),
    'Sample Standard Deviation': tpl.stdev(1, usFmt),
    Minimum: tpl.min(usFmt),
    Maximum: tpl.max(usFmt),
    // First: tpl.first(usFmt),
    // Last: tpl.last(usFmt),
    // 'Sum over Sum': tpl.sumOverSum(usFmt),
    'Sum as Fraction of Total': tpl.fractionOf(tpl.sum(), 'total', usFmtPct),
    'Sum as Fraction of Rows': tpl.fractionOf(tpl.sum(), 'row', usFmtPct),
    'Sum as Fraction of Columns': tpl.fractionOf(tpl.sum(), 'col', usFmtPct),
    'Count as Fraction of Total': tpl.fractionOf(tpl.count(), 'total', usFmtPct),
    'Count as Fraction of Rows': tpl.fractionOf(tpl.count(), 'row', usFmtPct),
    'Count as Fraction of Columns': tpl.fractionOf(tpl.count(), 'col', usFmtPct),
}))(aggregatorTemplates);

export const locales: any = {
    en: {
        aggregators,
        localeStrings: {
            renderError: 'An error occurred rendering the PivotTable results.',
            computeError: 'An error occurred computing the PivotTable results.',
            uiRenderError: 'An error occurred rendering the PivotTable UI.',
            selectAll: 'Select All',
            selectNone: 'Select None',
            tooMany: '(too many to list)',
            filterResults: 'Filter values',
            apply: 'Apply',
            cancel: 'Cancel',
            totals: 'Totals',
            vs: 'vs',
            by: 'by',
        },
    },
};

const mthNamesEn = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
const dayNamesEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const zeroPad = (number: number) => ('0' + number).substr(-2, 2);

export const derivers = {
    bin(col: string, binWidth: number) {
        return (record: any) => record[col] - (record[col] % binWidth);
    },
    dateFormat(
        col: string,
        formatString: string,
        utcOutput = false,
        mthNames = mthNamesEn,
        dayNames = dayNamesEn
    ) {
        const utc = utcOutput ? 'UTC' : '';
        return (record: any) => {
            const date = new Date(Date.parse(record[col]));
            if (isNaN(date.getTime())) {
                return '';
            }
            return formatString.replace(/%(.)/g, (m, p) => {
                switch (p) {
                    case 'y':
                        return (date as any)['get' + utc + 'FullYear']();
                    case 'm':
                        return zeroPad((date as any)['get' + utc + 'Month']() + 1);
                    case 'n':
                        return mthNames[(date as any)['get' + utc + 'Month']()];
                    case 'd':
                        return zeroPad((date as any)['get' + utc + 'Date']());
                    case 'w':
                        return dayNames[(date as any)['get' + utc + 'Day']()];
                    case 'x':
                        return (date as any)['get' + utc + 'Day']();
                    case 'H':
                        return zeroPad((date as any)['get' + utc + 'Hours']());
                    case 'M':
                        return zeroPad((date as any)['get' + utc + 'Minutes']());
                    case 'S':
                        return zeroPad((date as any)['get' + utc + 'Seconds']());
                    default:
                        return '%' + p;
                }
            });
        };
    },
};

export class PivotData {
    props: any;
    aggregator: any;
    tree: any;
    rowKeys: any[][];
    colKeys: any[][];
    rowTotals: any;
    colTotals: any;
    allTotal: any;
    sorted: boolean;

    static propTypes: any = {
        data: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.func])
            .isRequired,
        aggregatorName: PropTypes.string,
        cols: PropTypes.arrayOf(PropTypes.string),
        rows: PropTypes.arrayOf(PropTypes.string),
        vals: PropTypes.arrayOf(PropTypes.string),
        valueFilter: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
        sorters: PropTypes.oneOfType([
            PropTypes.objectOf(PropTypes.func),
            PropTypes.func,
        ]),
        derivedAttributes: PropTypes.objectOf(PropTypes.func),
        rowOrder: PropTypes.oneOf(['key_a_to_z', 'value_a_to_z', 'value_z_to_a']),
        colOrder: PropTypes.oneOf(['key_a_to_z', 'value_a_to_z', 'value_z_to_a']),
    };

    static defaultProps: any = {
        aggregators,
        renderers: {},
        rows: [],
        cols: [],
        vals: [],
        valueFilter: {},
        aggregatorName: 'Count',
        sorters: {},
        derivedAttributes: {},
        rowOrder: 'key_a_to_z',
        colOrder: 'key_a_to_z',
    };

    constructor(inputProps = {}) {
        this.props = Object.assign({}, PivotData.defaultProps, inputProps);
        // (PropTypes as any).checkPropTypes(PivotData.propTypes, this.props, 'prop', 'PivotData');

        this.aggregator = this.props.aggregators[this.props.aggregatorName](
            this.props.vals
        );
        this.tree = {};
        this.rowKeys = [];
        this.colKeys = [];
        this.rowTotals = {};
        this.colTotals = {};
        this.allTotal = this.aggregator(this, [], []);
        this.sorted = false;

        this.partialRowTotals = {};
        this.partialTree = {};

        // iterate through input, accumulating data for cells
        PivotData.forEachRecord(
            this.props.data,
            this.props.derivedAttributes,
            record => {
                if (this.filter(record)) {
                    this.processRecord(record);
                }
            }
        );
    }

    filter(record: any) {
        for (const k in this.props.valueFilter) {
            if (record[k] in this.props.valueFilter[k]) {
                return false;
            }
        }
        return true;
    }

    forEachMatchingRecord(criteria: any, callback: any) {
        return PivotData.forEachRecord(
            this.props.data,
            this.props.derivedAttributes,
            record => {
                if (!this.filter(record)) {
                    return;
                }
                for (const k in criteria) {
                    const v = criteria[k];
                    if (v !== (k in record ? record[k] : 'null')) {
                        return;
                    }
                }
                callback(record);
            }
        );
    }

    arrSort(attrs: any[]) {
        const sortersArr = Array.from(attrs).map(a =>
            getSort(this.props.sorters, a)
        );
        return (a: any[], b: any[]) => {
            for (const i of Object.keys(sortersArr) as any) {
                const sorter = sortersArr[i];
                const comparison = sorter(a[i], b[i]);
                if (comparison !== 0) {
                    return comparison;
                }
            }
            return 0;
        };
    }

    sortKeys() {
        if (!this.sorted) {
            this.sorted = true;
            const v = (r: any[], c: any[]) => this.getAggregator(r, c).value();
            switch (this.props.rowOrder) {
                case 'value_a_to_z':
                    this.rowKeys.sort((a, b) => naturalSort(v(a, []), v(b, [])));
                    break;
                case 'value_z_to_a':
                    this.rowKeys.sort((a, b) => -naturalSort(v(a, []), v(b, [])));
                    break;
                default:
                    this.rowKeys.sort(this.arrSort(this.props.rows));
            }
            switch (this.props.colOrder) {
                case 'value_a_to_z':
                    this.colKeys.sort((a, b) => naturalSort(v([], a), v([], b)));
                    break;
                case 'value_z_to_a':
                    this.colKeys.sort((a, b) => -naturalSort(v([], a), v([], b)));
                    break;
                default:
                    this.colKeys.sort(this.arrSort(this.props.cols));
            }
        }
    }

    getColKeys() {
        this.sortKeys();
        return this.colKeys;
    }

    getRowKeys() {
        this.sortKeys();
        return this.rowKeys;
    }

    processRecord(record: any) {
        const colKey = Array.from(this.props.cols as any[]).map(x =>
            x in record ? record[x] : 'null'
        );
        const rowKey = Array.from(this.props.rows as any[]).map(x =>
            x in record ? record[x] : 'null'
        );

        const flatRowKey = rowKey.join(String.fromCharCode(0));
        const flatColKey = colKey.join(String.fromCharCode(0));

        this.allTotal.push(record);

        if (rowKey.length !== 0) {
            if (!this.rowTotals[flatRowKey]) {
                this.rowKeys.push(rowKey);
                this.rowTotals[flatRowKey] = this.aggregator(this, rowKey, []);
            }
            this.rowTotals[flatRowKey].push(record);
        }

        if (colKey.length !== 0) {
            if (!this.colTotals[flatColKey]) {
                this.colKeys.push(colKey);
                this.colTotals[flatColKey] = this.aggregator(this, [], colKey);
            }
            this.colTotals[flatColKey].push(record);
        }

        if (colKey.length !== 0 && rowKey.length !== 0) {
            if (!this.tree[flatRowKey]) {
                this.tree[flatRowKey] = {};
            }
            if (!this.tree[flatRowKey][flatColKey]) {
                this.tree[flatRowKey][flatColKey] = this.aggregator(this, rowKey, colKey);
            }
            this.tree[flatRowKey][flatColKey].push(record);
        }
    }

    getAggregator(rowKey: any[], colKey: any[]) {
        let agg;
        const isPartialRow = rowKey.length > 0 && rowKey.length < this.props.rows.length;
        const flatRowKey = rowKey.join(String.fromCharCode(0));
        const flatColKey = colKey.join(String.fromCharCode(0));
        if (rowKey.length === 0 && colKey.length === 0) {
            agg = this.allTotal;
        } else if (rowKey.length === 0) {
            agg = this.colTotals[flatColKey];
        } else if (colKey.length === 0) {
            agg = isPartialRow ? this.partialRowTotals[flatRowKey] : this.rowTotals[flatRowKey];
        } else {
            agg = isPartialRow 
                ? (this.partialTree[flatRowKey] ? this.partialTree[flatRowKey][flatColKey] : undefined)
                : (this.tree[flatRowKey] ? this.tree[flatRowKey][flatColKey] : undefined);
        }
        return (
            agg || {
                value() {
                    return null;
                },
                format() {
                    return '';
                },
            }
        );
    }

    static forEachRecord(input: any, derivedAttributes: any, f: any) {
        let addRecord;
        if (Object.getOwnPropertyNames(derivedAttributes).length === 0) {
            addRecord = f;
        } else {
            addRecord = function (record: any) {
                for (const k in derivedAttributes) {
                    const derived = derivedAttributes[k](record);
                    if (derived !== null) {
                        record[k] = derived;
                    }
                }
                return f(record);
            };
        }

        if (typeof input === 'function') {
            input(addRecord);
        } else {
            input.forEach(addRecord);
        }
    }
}

export default {
    aggregatorTemplates,
    aggregators,
    derivers,
    locales,
    naturalSort,
    numberFormat,
    getSort,
    sortAs,
    PivotData,
};
